import { useState, useEffect } from 'react';
import { AlertTriangle, X, ChevronDown, ChevronUp, Clock, CalendarX2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { gymApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface ExpiringMembership {
    membership_id: string;
    client_name: string;
    end_date: string;
    is_expired: boolean;
}

export function MembershipAlertsBanner() {
    const { user, gym, hasRole } = useAuth();
    const [memberships, setMemberships] = useState<ExpiringMembership[]>([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Only show for gym-level roles
    const isGymStaff = hasRole(['gym_admin', 'gym_staff', 'gym_supervisor']);
    const alertsDisabled = (user as any)?.alerts_disabled === true;

    useEffect(() => {
        if (!gym?.gym_id || !isGymStaff || alertsDisabled) {
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                const res = await gymApi.getExpiringMemberships(gym.gym_id, 7);
                if (res.success) {
                    setMemberships(res.memberships);
                }
            } catch (error) {
                console.error('Failed to fetch expiring memberships:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [gym?.gym_id, isGymStaff, alertsDisabled]);

    // Don't render if not applicable
    if (!isGymStaff || alertsDisabled || isDismissed || isLoading || memberships.length === 0) {
        return null;
    }

    const expiredCount = memberships.filter(m => m.is_expired).length;
    const expiringSoon = memberships.filter(m => !m.is_expired);
    const expired = memberships.filter(m => m.is_expired);

    return (
        <div className={cn(
            "mx-4 mb-4 rounded-xl border overflow-hidden transition-all",
            expiredCount > 0 ? "bg-red-500/5 border-red-500/30" : "bg-orange-500/5 border-orange-500/30"
        )}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 gap-3">
                <div className="flex items-center gap-3">
                    <div className={cn(
                        "p-2 rounded-lg",
                        expiredCount > 0 ? "bg-red-500/10 text-red-500" : "bg-orange-500/10 text-orange-500"
                    )}>
                        <AlertTriangle className="h-5 w-5" />
                    </div>
                    <div>
                        <p className="font-bold text-sm">
                            {expiredCount > 0
                                ? `${expiredCount} membresía(s) vencida(s)`
                                : `${expiringSoon.length} membresía(s) por vencer`
                            }
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {expiredCount > 0 && expiringSoon.length > 0
                                ? `${expiringSoon.length} más por vencer pronto`
                                : expiredCount > 0 ? 'Requieren renovación inmediata' : 'En los próximos 7 días'
                            }
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-3 text-xs font-bold"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                        {isExpanded ? 'Ocultar' : 'Ver detalles'}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => setIsDismissed(true)}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Expanded List */}
            {isExpanded && (
                <div className="border-t border-border/50 p-3 space-y-4 max-h-64 overflow-y-auto bg-slate-50/50 dark:bg-black/20">
                    {/* Already Expired Section */}
                    {expired.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-[10px] uppercase font-bold text-red-500 tracking-wider flex items-center gap-1.5 ml-1">
                                <CalendarX2 className="h-3 w-3" /> Vencidas
                            </p>
                            {expired.map((m) => (
                                <div key={m.membership_id} className="flex items-center justify-between p-2 rounded-lg text-sm bg-red-500/10 border border-red-500/10">
                                    <span className="font-medium">{m.client_name}</span>
                                    <Badge variant="destructive" className="text-[10px] h-5">Vencida</Badge>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Expiring Soon Section */}
                    {expiringSoon.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-[10px] uppercase font-bold text-orange-500 tracking-wider flex items-center gap-1.5 ml-1">
                                <Clock className="h-3 w-3" /> Por vencer
                            </p>
                            {expiringSoon.map((m) => (
                                <div key={m.membership_id} className="flex items-center justify-between p-2 rounded-lg text-sm bg-orange-500/10 border border-orange-500/10">
                                    <span className="font-medium">{m.client_name}</span>
                                    <Badge variant="outline" className="text-[10px] h-5 border-orange-500/30 text-orange-600 dark:text-orange-400">
                                        Vence {new Date(m.end_date).toLocaleDateString('es-ES')}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
